import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth, API } from '../App';

/**
 * useAuthorityProfile — Iter 205a.
 *
 * Detects whether the logged-in user is an admin-created Official Safety
 * Source account (Town Hall / Police) and exposes the fields the various
 * authority-aware UI surfaces need:
 *
 *   • Authority Mini-Dashboard at /safety-bulletin
 *   • Profile page authority block
 *   • First-login welcome toast
 *   • Bottom-nav amber indicator
 *
 * Returns `{ loading, isOfficial, source, town, displayName, followers,
 * publishedLast30d }`. Defaults are safe for SSR / signed-out users.
 *
 * Why a dedicated hook?
 *   The global `useAuth()` only stores `{ token, role, userId }` and is
 *   shared by anonymous users. Adding authority fields there would risk
 *   regressions on the resident flow. A separate hook keeps the surface
 *   minimal and the existing auth contract untouched.
 */
export default function useAuthorityProfile() {
  const { user } = useAuth();
  const [state, setState] = useState({
    loading: !!user?.token,
    isOfficial: false,
    source: null,
    town: '',
    displayName: '',
    followers: 0,
    publishedLast30d: 0,
  });

  useEffect(() => {
    if (!user?.token) {
      setState({
        loading: false, isOfficial: false, source: null,
        town: '', displayName: '', followers: 0, publishedLast30d: 0,
      });
      return;
    }
    let alive = true;
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${user.token}` };
        const prof = await axios.get(`${API}/user/profile`, { headers });
        const p = prof.data || {};
        const isOfficial = p.officialSafetySource === 'town_hall' || p.officialSafetySource === 'police';
        if (!isOfficial) {
          if (alive) setState(s => ({ ...s, loading: false, isOfficial: false }));
          return;
        }
        // Pull the public authority directory row so we can show the
        // follower + 30d-published counts on every authority surface
        // without making each one re-implement the aggregation.
        let followers = 0;
        let publishedLast30d = 0;
        try {
          const dir = await axios.get(`${API}/safety-bulletin/authorities`);
          const me = (dir.data?.authorities || []).find(a => a.userId === p.id);
          if (me) {
            followers = me.followers || 0;
            publishedLast30d = me.published30d || 0;
          }
        } catch { /* directory is best-effort */ }
        if (alive) {
          setState({
            loading: false, isOfficial: true,
            source: p.officialSafetySource,
            town: p.officialSafetyTown || '',
            displayName: p.fullName || '',
            followers, publishedLast30d,
          });
        }
      } catch {
        if (alive) setState(s => ({ ...s, loading: false, isOfficial: false }));
      }
    })();
    return () => { alive = false; };
  }, [user?.token]);

  return state;
}
